import { FC, ReactNode, useEffect } from "react";
import {
  Row,
  Box,
  Column,
  Button,
  Text,
  Heading,
  ArrowRightIcon,
  HtImage,
} from "@hightouch/htui";
import { useNavigate } from "src/router";
import { LogoHeader } from "src/components/layout";
import arrowIcon from "src/components/welcome/arrow.svg";
import arrowsIcon from "src/components/welcome/arrows.svg";
import databaseIcon from "src/components/welcome/database.svg";
import { CreateWorkspace } from "src/components/workspaces/create-workspace";
import { useAccessibleOrganizationsQuery } from "src/graphql";
import * as analytics from "src/lib/analytics";
import { Card } from "src/components/card";
import { PageSpinner } from "src/components/loading";
import { cdn } from "src/utils/sanity";
import { switchWorkspace } from "src/utils/workspaces";
import { GlowLogo } from "src/components/auth/glow-logo";

export const Welcome: FC = () => {
  const navigate = useNavigate();

  const { data: organizations, isLoading } = useAccessibleOrganizationsQuery(
    {},
    { select: (data) => data.getAccessibleOrganizations.organizations },
  );

  if (isLoading) {
    return <PageSpinner />;
  }

  return (
    <Column align="center" height="100vh" width="100vw">
      <LogoHeader />
      <Column align="center" bg="gray.50" flex={1} py={10} width="100%">
        <Diagram
          onNext={() => {
            if (organizations?.length) {
              navigate("/workspaces");
            } else {
              navigate("/welcome/create-workspace");
            }
          }}
        />
      </Column>
    </Column>
  );
};

export const WelcomeWorkspaceSetup: FC = () => {
  useEffect(() => {
    analytics.track("Onboarding Started", {});
  }, []);

  return (
    <Column align="center" height="100vh" width="100vw">
      <LogoHeader />
      <Column
        align="center"
        bg="gray.50"
        flex={1}
        overflowY="auto"
        py={10}
        width="100%"
        height="100%"
      >
        <CreateYourFirstWorkspace />
      </Column>
    </Column>
  );
};

const Step: FC<{
  title: string;
  description?: string;
  children: ReactNode;
  width?: string;
}> = ({ title, description, children, width = "666px" }) => {
  return (
    <Column align="center" flex={1} maxWidth={width} width="100%" height="100%">
      <Heading mb={4} size="2xl" textAlign="center">
        {title}
      </Heading>
      {description && (
        <Box
          color="text.tertiary"
          fontSize="lg"
          maxWidth="56ch"
          textAlign="center"
        >
          {description}
        </Box>
      )}
      <Column
        align="center"
        flex={1}
        gap={10}
        width="100%"
        mt={10}
        overflowY="auto"
        minHeight={0}
      >
        {children}
      </Column>
    </Column>
  );
};

const Diagram: FC<Readonly<{ onNext: () => void }>> = ({ onNext }) => {
  return (
    <Step
      description="Hightouch is the easiest way to sync data into the tools that move your business — no scripts, CSVs, or
      engineering favors required."
      title="Welcome to Hightouch"
      width="100%"
    >
      <Row
        align="center"
        gap={8}
        width="100%"
        maxWidth="1000px"
        px={4}
        justify="center"
      >
        <Column align="center" gap={4} justify="center">
          <HtImage
            src={databaseIcon}
            alt="Database icon"
            boxSize="72px"
            mt={8} // Top margin centers logo with flow chart
          />
          <Heading size="md">Data warehouse</Heading>
        </Column>

        <HtImage src={arrowIcon} alt="Arrow icon" />

        <Column align="center" gap={4} justify="center">
          <GlowLogo
            mt={8} // Top margin centers logo with flow chart
          />
          <Heading size="md">Hightouch</Heading>
        </Column>

        <Box minWidth="250px">
          <HtImage src={arrowsIcon} alt="Arrows icon" />
        </Box>

        <Column gap={5}>
          <DiagramCard label="Advertising" logos={advertistingLogos} />
          <DiagramCard label="Marketing automation" logos={marketingLogos} />
          <DiagramCard label="CRM" logos={crmLogos} />
          <DiagramCard label="Customer service" logos={customerSuccessLogos} />
        </Column>
      </Row>

      <Button
        mt={10}
        size="lg"
        variant="primary"
        onClick={onNext}
        directionIcon={ArrowRightIcon}
      >
        Get started
      </Button>
    </Step>
  );
};

const DiagramCard: FC<{ label: string; logos: Record<string, string> }> = ({
  label,
  logos,
}) => (
  <Column gap={3} justify="center" py={2}>
    <Row align="center" gap={4}>
      {Object.values(logos).map((src) => (
        <Column
          key={src}
          borderRadius="24px"
          bg="white"
          boxShadow="md"
          height="48px"
          width="48px"
          align="center"
          justify="center"
        >
          <HtImage height="25px" src={cdn(src, 50)} alt={`${label} logo`} />
        </Column>
      ))}
    </Row>
    <Text fontWeight="medium">{label}</Text>
  </Column>
);

const CreateYourFirstWorkspace: FC = () => {
  return (
    <Step title="Create your first workspace">
      <Card p={6} width="536px">
        <CreateWorkspace
          onboarding
          onSubmit={async ({ id, slug }) => {
            await switchWorkspace(id, `/${slug}/onboarding`);
          }}
        />
      </Card>
    </Step>
  );
};

const crmLogos = {
  salesforce: "e4fa4b01bbc006d21f215f7c629fb4120eaeaeb4-512x512.svg",
  salesloft: "8dcafcb6ab137e258c31b2729f222fc513d5a85b-512x512.png",
  freshsales: "d5adbaf049faf7c175c0516eb3407b3784556274-512x512.svg",
};

const advertistingLogos = {
  facebook: "a90feacc04182f95fa6daf663a2a9861b470857c-1365x1365.svg",
  twitter: "694b09db15808f6965e208a46c87bb7f27ca6770-518x518.png",
  googleAds: "f423bac1e966b640cfcbea01bfcbef9e11021f4b-1200x1089.png",
};

const customerSuccessLogos = {
  zendesk: "29158c9660b8eb9202c66c315f7a5f5bc510049f-512x512.svg",
  intercom: "ecb8820a379b770f7be348a4d598e45deac06b6d-2500x2500.svg",
  slack: "661acab40576a1acc6018d2f902e2a6186cd530e-512x512.png",
};

const marketingLogos = {
  braze: "dd757379e84ce72a0ebf486dd63659c00af4abed-512x512.svg",
  hubspot: "403a4633d60d71d0ccb5bc5be4b4ff4a7e5bfc2c-2500x2500.svg",
  iterable: "8069e1ed66b07e9047e685a6edbd1c75e433f33f-1010x1010.png",
};
